/**
 * Super Admin — AI Import tool.
 *
 * Full interactive import workflow:
 *   - Create new import job (provisions a staging workspace)
 *   - File upload (CSV/XLSX ingestion)
 *   - Column mapping editor with AI suggestions
 *   - Dry-run preview
 *   - Commit (flip staging to live) or Abort
 *   - Audit timeline
 *
 * Read-only job history lives at /platform/imports-migrations.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Upload,
  Plus,
  Eye,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ImportJob {
  id: string;
  sourcePlatform: string;
  sourceLabel: string | null;
  targetWorkspaceId: string;
  status:
    | "draft"
    | "ingesting"
    | "mapping"
    | "dry_run"
    | "committing"
    | "committed"
    | "aborted";
  operatorUserId: string;
  notes: string | null;
  createdAt: string;
  committedAt: string | null;
  abortedAt: string | null;
}

interface ImportFile {
  id: string;
  jobId: string;
  fileName: string;
  objectPath: string;
  fileType: string;
  rowCount: number | null;
  headerColumns: string[] | null;
  inventory: Record<
    string,
    {
      nonNullCount: number;
      distinctCount: number;
      samples: string[];
      inferredType: string;
    }
  >;
  uploadedAt: string;
}

interface JobDecision {
  id: string;
  jobId: string;
  fileId: string;
  sourceField: string;
  targetTable: "leads" | "clients";
  targetColumn: string;
  transformKind: string;
  transformConfig: Record<string, unknown>;
  confidence: string | null;
}

interface Suggestion {
  sourceField: string;
  targetTable: string;
  targetColumn: string;
  transformKind: string;
  transformConfig: Record<string, unknown>;
  confidence: number;
  reason: "memory" | "heuristic";
  memoryId?: string;
  usageCount?: number;
}

interface DraftDecision {
  sourceField: string;
  targetTable: "leads" | "clients" | "__skip__";
  targetColumn: string;
  transformKind: string;
  confidence?: number;
  rememberNow: boolean;
  reason?: "memory" | "heuristic";
}

const TARGET_OPTIONS: Record<
  "leads" | "clients",
  Array<{ value: string; label: string }>
> = {
  leads: [
    { value: "leadName", label: "Lead / venue name" },
    { value: "contactName", label: "Contact name" },
    { value: "emailAddress", label: "Email" },
    { value: "phoneNumber", label: "Phone" },
    { value: "address", label: "Address" },
    { value: "city", label: "City" },
    { value: "state", label: "State" },
    { value: "zipCode", label: "Zip" },
    { value: "status", label: "Status" },
    { value: "source", label: "Source" },
    { value: "rep", label: "Rep" },
    { value: "notes", label: "Notes" },
    { value: "website", label: "Website" },
    { value: "venueType", label: "Venue type" },
    { value: "venueCapacity", label: "Venue capacity" },
  ],
  clients: [
    { value: "accountName", label: "Account / venue name" },
    { value: "contactName", label: "Contact name" },
    { value: "emailAddress", label: "Email" },
    { value: "phoneNumber", label: "Phone" },
    { value: "mobilePhone", label: "Mobile phone" },
    { value: "address", label: "Address" },
    { value: "city", label: "City" },
    { value: "state", label: "State" },
    { value: "zipCode", label: "Zip" },
    { value: "rate", label: "Rate" },
    { value: "dateOfEvent", label: "Date of event" },
    { value: "eventTime", label: "Event time" },
    { value: "rep", label: "Rep" },
    { value: "notes", label: "Notes" },
    { value: "venueType", label: "Venue type" },
  ],
};

const TRANSFORM_OPTIONS = [
  { value: "direct", label: "Trim only" },
  { value: "lower", label: "Lowercase" },
  { value: "upper", label: "Uppercase" },
  { value: "email_lower", label: "Email (validate + lowercase)" },
  { value: "phone_e164", label: "Phone → E.164" },
  { value: "date_iso", label: "Date → ISO" },
];

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-500",
  ingesting: "bg-amber-500",
  mapping: "bg-blue-500",
  dry_run: "bg-indigo-500",
  committing: "bg-purple-500",
  committed: "bg-emerald-600",
  aborted: "bg-rose-600",
};

export function AiImportContent() {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  if (selectedJobId) {
    return (
      <>
        <JobDetailView
          jobId={selectedJobId}
          onBack={() => setSelectedJobId(null)}
        />
      </>
    );
  }
  return (
    <>
      <JobListView onSelect={setSelectedJobId} />
    </>
  );
}

export default function PlatformAiImport() {
  return <PlatformLayout><AiImportContent /></PlatformLayout>;
}

// ─── List view ────────────────────────────────────────────────────────────────

function JobListView({ onSelect }: { onSelect: (id: string) => void }) {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    workspaceName: "",
    workspaceSlug: "",
    sourcePlatform: "spreadsheet",
    sourceLabel: "",
    notes: "",
  });

  const { data: jobs, isLoading } = useQuery<ImportJob[]>({
    queryKey: ["/api/platform/onboarding/jobs"],
  });

  const createMut = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/platform/onboarding/jobs", {
        ...form,
        sourceLabel: form.sourceLabel || undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: async (res: any) => {
      const data = await res.json();
      toast({
        title: "Import job created",
        description: `Staging workspace "${data.workspace?.slug}" provisioned`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs"],
      });
      setCreateOpen(false);
      setForm({
        workspaceName: "",
        workspaceSlug: "",
        sourcePlatform: "spreadsheet",
        sourceLabel: "",
        notes: "",
      });
      onSelect(data.job.id);
    },
    onError: (err: any) => {
      toast({
        title: "Could not create job",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6 p-6" data-testid="page-ai-import">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Import</h1>
          <p className="text-sm text-muted-foreground">
            White-glove migrations from CSV/XLSX into a staging workspace, then
            atomic flip to live.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-import">
              <Plus className="h-4 w-4 mr-2" /> New import
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create import job</DialogTitle>
              <DialogDescription>
                A new workspace is provisioned in <em>staging</em> mode. All
                side-effects are suppressed until you commit. Slug must be
                lowercase letters, digits, and hyphens.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="workspaceName">Workspace name</Label>
                <Input
                  id="workspaceName"
                  data-testid="input-workspace-name"
                  value={form.workspaceName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, workspaceName: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="workspaceSlug">Workspace slug</Label>
                <Input
                  id="workspaceSlug"
                  data-testid="input-workspace-slug"
                  value={form.workspaceSlug}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      workspaceSlug: e.target.value.toLowerCase(),
                    }))
                  }
                  placeholder="acme-music"
                />
              </div>
              <div>
                <Label htmlFor="sourcePlatform">Source platform</Label>
                <Input
                  id="sourcePlatform"
                  data-testid="input-source-platform"
                  value={form.sourcePlatform}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sourcePlatform: e.target.value }))
                  }
                  placeholder="hubspot, salesforce, spreadsheet…"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Free text. The mapping memory keys off this string, so use the
                  same value for every migration from the same source.
                </p>
              </div>
              <div>
                <Label htmlFor="sourceLabel">
                  Label (e.g. "Q1 2026 BatonRouge import")
                </Label>
                <Input
                  id="sourceLabel"
                  data-testid="input-source-label"
                  value={form.sourceLabel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sourceLabel: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="notes">Internal notes</Label>
                <Textarea
                  id="notes"
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="button-create-job"
                disabled={
                  createMut.isPending ||
                  !form.workspaceName ||
                  !form.workspaceSlug
                }
                onClick={() => createMut.mutate()}
              >
                {createMut.isPending
                  ? "Creating…"
                  : "Create job + staging workspace"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Import jobs</CardTitle>
          <CardDescription>{jobs?.length ?? 0} total</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !jobs || jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No import jobs yet. Create one to begin.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.id} data-testid={`row-job-${j.id}`}>
                    <TableCell>{j.sourcePlatform}</TableCell>
                    <TableCell>
                      {j.sourceLabel ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={STATUS_COLORS[j.status] ?? "bg-slate-500"}
                      >
                        {j.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(j.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSelect(j.id)}
                        data-testid={`button-open-${j.id}`}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Detail view ──────────────────────────────────────────────────────────────

interface JobDetailResponse {
  job: ImportJob;
  files: ImportFile[];
  decisions: JobDecision[];
}

function JobDetailView({
  jobId,
  onBack,
}: {
  jobId: string;
  onBack: () => void;
}) {
  const { toast } = useToast();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery<JobDetailResponse>({
    queryKey: ["/api/platform/onboarding/jobs", jobId],
  });

  const isTerminal =
    data?.job.status === "committed" || data?.job.status === "aborted";
  useEffect(() => {
    if (data?.files?.length && !activeFileId)
      setActiveFileId(data.files[0].id);
  }, [data?.files, activeFileId]);

  // ── File upload ──
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const presignRes = await apiRequest(
        "POST",
        "/api/platform/onboarding/upload-url",
        { name: file.name, size: file.size }
      );
      const { uploadURL, objectPath } = await presignRes.json();
      const putRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!putRes.ok) throw new Error("Upload to storage failed");
      const regRes = await apiRequest(
        "POST",
        `/api/platform/onboarding/jobs/${jobId}/files`,
        {
          fileName: file.name,
          objectPath,
          fileType: file.name.toLowerCase().match(/\.xlsx?$/) ? "xlsx" : "csv",
        }
      );
      const { file: registered } = await regRes.json();
      toast({
        title: "File ingested",
        description: `${file.name} — ${registered.rowCount} rows`,
      });
      setActiveFileId(registered.id);
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs", jobId],
      });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const dryRunMut = useMutation({
    mutationFn: async () =>
      apiRequest(
        "POST",
        `/api/platform/onboarding/jobs/${jobId}/dry-run`,
        {}
      ),
    onSuccess: async (res) => {
      const preview = await res.json();
      setPreviewData(preview);
      setPreviewOpen(true);
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs", jobId],
      });
    },
    onError: (err: any) =>
      toast({
        title: "Dry-run failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const commitMut = useMutation({
    mutationFn: async () =>
      apiRequest(
        "POST",
        `/api/platform/onboarding/jobs/${jobId}/commit`,
        {}
      ),
    onSuccess: async (res) => {
      const result = await res.json();
      const totals = Object.entries(
        result.rowsByTable as Record<string, number>
      )
        .map(([t, n]) => `${n} ${t}`)
        .join(", ");
      toast({
        title: "Committed!",
        description: `Workspace is live. Inserted: ${totals || "0 rows"}`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs", jobId],
      });
    },
    onError: (err: any) =>
      toast({
        title: "Commit failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const abortMut = useMutation({
    mutationFn: async (reason: string) =>
      apiRequest(
        "POST",
        `/api/platform/onboarding/jobs/${jobId}/abort`,
        { reason }
      ),
    onSuccess: () => {
      toast({
        title: "Job aborted",
        description: "Staging workspace dropped.",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs", jobId],
      });
    },
    onError: (err: any) =>
      toast({
        title: "Abort failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  if (isLoading || !data) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid={`page-job-${jobId}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            data-testid="button-back"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              Import — {data.job.sourcePlatform}
            </h1>
            <p className="text-sm text-muted-foreground">
              {data.job.sourceLabel ?? "no label"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={STATUS_COLORS[data.job.status]}
            data-testid={`badge-status-${data.job.status}`}
          >
            {data.job.status}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Files</CardTitle>
          <CardDescription>
            CSV (and TSV) parsed natively. XLSX supported via SheetJS — first
            sheet only in Phase 1.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!isTerminal && (
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".csv,.tsv,.xlsx,.xls"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
                data-testid="input-upload-file"
              />
              {uploading && (
                <span className="text-sm text-muted-foreground">
                  Uploading &amp; ingesting…
                </span>
              )}
            </div>
          )}
          <div className="space-y-2">
            {data.files.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No files uploaded yet.
              </p>
            ) : (
              data.files.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setActiveFileId(f.id)}
                  className={`w-full text-left rounded border p-3 transition ${
                    activeFileId === f.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted"
                  }`}
                  data-testid={`button-file-${f.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{f.fileName}</div>
                      <div className="text-xs text-muted-foreground">
                        {f.fileType.toUpperCase()} · {f.rowCount ?? "?"} rows ·{" "}
                        {f.headerColumns?.length ?? 0} columns
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {activeFileId && data.files.find((f) => f.id === activeFileId) && (
        <FileMappingEditor
          jobId={jobId}
          file={data.files.find((f) => f.id === activeFileId)!}
          existingDecisions={data.decisions.filter(
            (d) => d.fileId === activeFileId
          )}
          isTerminal={!!isTerminal}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Commit</CardTitle>
          <CardDescription>
            Dry-run produces a 50-row preview per target table. Commit applies
            all decisions to the staging workspace and atomically flips it live.
            Phase 1 inserts into <code>leads</code> and <code>clients</code>{" "}
            only.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button
            onClick={() => dryRunMut.mutate()}
            disabled={dryRunMut.isPending || isTerminal}
            data-testid="button-dry-run"
          >
            <Eye className="h-4 w-4 mr-2" />{" "}
            {dryRunMut.isPending ? "Generating preview…" : "Dry-run preview"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                disabled={isTerminal || commitMut.isPending}
                data-testid="button-commit"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />{" "}
                {commitMut.isPending ? "Committing…" : "Commit & flip live"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Commit this import?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will insert all mapped rows into the staging workspace
                  and atomically flip it to live. The workspace becomes a real,
                  side-effect-enabled workspace immediately. This cannot be
                  undone via the abort button.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => commitMut.mutate()}>
                  Commit
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                disabled={isTerminal || abortMut.isPending}
                data-testid="button-abort"
              >
                <XCircle className="h-4 w-4 mr-2" /> Abort
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Abort import?</AlertDialogTitle>
                <AlertDialogDescription>
                  The staging workspace will be deleted along with any rows
                  inserted during dry-run testing.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    abortMut.mutate("Operator aborted via UI")
                  }
                >
                  Abort import
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      <AuditTimeline jobId={jobId} />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Dry-run preview</DialogTitle>
            <DialogDescription>
              {previewData?.totalSourceRows ?? 0} source rows scanned. Showing
              up to 50 rows per target table.
            </DialogDescription>
          </DialogHeader>
          {previewData?.warnings?.length > 0 && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" /> Warnings
              </div>
              <ul className="list-disc ml-6 mt-1">
                {previewData.warnings.map((w: string, i: number) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          <ScrollArea className="max-h-[60vh]">
            {previewData &&
              Object.entries(
                previewData.byTable as Record<
                  string,
                  Array<Record<string, string | null>>
                >
              ).map(([table, rows]) => (
                <div key={table} className="mb-6">
                  <h3 className="font-semibold mb-2">
                    {table} ({rows.length})
                  </h3>
                  {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No mapped rows.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {Object.keys(rows[0]).map((c) => (
                              <TableHead key={c}>{c}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r, i) => (
                            <TableRow key={i}>
                              {Object.keys(rows[0]).map((c) => (
                                <TableCell
                                  key={c}
                                  className="text-xs max-w-[200px] truncate"
                                >
                                  {r[c] ?? (
                                    <span className="text-muted-foreground">
                                      ∅
                                    </span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Mapping editor for one file ──────────────────────────────────────────────

function FileMappingEditor({
  jobId,
  file,
  existingDecisions,
  isTerminal,
}: {
  jobId: string;
  file: ImportFile;
  existingDecisions: JobDecision[];
  isTerminal: boolean;
}) {
  const { toast } = useToast();
  const { data: suggestionsData, isLoading: loadingSuggestions } = useQuery<{
    suggestions: Suggestion[];
    thresholds: { autoApply: number; batchApprove: number };
  }>({
    queryKey: [
      "/api/platform/onboarding/jobs",
      jobId,
      "files",
      file.id,
      "suggestions",
    ],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/platform/onboarding/jobs/${jobId}/files/${file.id}/suggestions`
      );
      return res.json();
    },
  });

  const drafts = useMemo<Record<string, DraftDecision>>(() => {
    const out: Record<string, DraftDecision> = {};
    for (const col of file.headerColumns ?? []) {
      const existing = existingDecisions.find((d) => d.sourceField === col);
      if (existing) {
        out[col] = {
          sourceField: col,
          targetTable: existing.targetTable,
          targetColumn: existing.targetColumn,
          transformKind: existing.transformKind,
          confidence: existing.confidence
            ? Number(existing.confidence)
            : undefined,
          rememberNow: false,
        };
      } else {
        const top = suggestionsData?.suggestions
          ?.filter((s) => s.sourceField === col)
          .sort((a, b) => b.confidence - a.confidence)[0];
        if (top) {
          out[col] = {
            sourceField: col,
            targetTable: top.targetTable as "leads" | "clients",
            targetColumn: top.targetColumn,
            transformKind: top.transformKind,
            confidence: top.confidence,
            rememberNow: false,
            reason: top.reason,
          };
        } else {
          out[col] = {
            sourceField: col,
            targetTable: "__skip__",
            targetColumn: "",
            transformKind: "direct",
            rememberNow: false,
          };
        }
      }
    }
    return out;
  }, [file.headerColumns, suggestionsData, existingDecisions]);

  const [edits, setEdits] = useState<Record<string, DraftDecision>>({});
  const merged = useMemo(() => ({ ...drafts, ...edits }), [drafts, edits]);

  const update = (col: string, patch: Partial<DraftDecision>) => {
    setEdits((e) => ({ ...e, [col]: { ...merged[col], ...patch } }));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const decisions = Object.values(merged)
        .filter((d) => d.targetTable !== "__skip__" && d.targetColumn)
        .map((d) => ({
          sourceField: d.sourceField,
          targetTable: d.targetTable as "leads" | "clients",
          targetColumn: d.targetColumn,
          transformKind: d.transformKind,
          transformConfig: {},
          confidence: d.confidence,
          rememberNow: d.rememberNow,
        }));
      return apiRequest(
        "PUT",
        `/api/platform/onboarding/jobs/${jobId}/decisions`,
        { fileId: file.id, decisions }
      );
    },
    onSuccess: async (res) => {
      const json = await res.json();
      toast({
        title: "Mappings saved",
        description: `${json.count} decisions saved${
          json.promoteWarnings?.length
            ? ` (${json.promoteWarnings.length} memory warnings)`
            : ""
        }`,
      });
      setEdits({});
      queryClient.invalidateQueries({
        queryKey: ["/api/platform/onboarding/jobs", jobId],
      });
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Map columns: {file.fileName}</CardTitle>
        <CardDescription>
          Suggestions come from mapping memory (prior confirmed migrations)
          first, then heuristics. Memory hits ≥{" "}
          {(
            (suggestionsData?.thresholds?.autoApply ?? 0.95) * 100
          ).toFixed(0)}
          % are auto-applied; below that you confirm.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingSuggestions ? (
          <p className="text-sm text-muted-foreground">
            Computing suggestions…
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source column</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Target table</TableHead>
                  <TableHead>Target column</TableHead>
                  <TableHead>Transform</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Remember</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(file.headerColumns ?? []).map((col) => {
                  const d = merged[col];
                  const inv = file.inventory?.[col];
                  return (
                    <TableRow key={col} data-testid={`row-mapping-${col}`}>
                      <TableCell
                        className="font-mono text-xs max-w-[180px] truncate"
                        title={col}
                      >
                        {col}
                      </TableCell>
                      <TableCell
                        className="text-xs text-muted-foreground max-w-[180px] truncate"
                        title={inv?.samples?.join(" | ")}
                      >
                        {inv?.samples?.slice(0, 2).join(" | ") || "—"}
                        {inv?.inferredType && (
                          <div className="text-[10px] uppercase">
                            {inv.inferredType}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          disabled={isTerminal}
                          value={d.targetTable}
                          onValueChange={(v) =>
                            update(col, {
                              targetTable: v as any,
                              targetColumn: "",
                            })
                          }
                        >
                          <SelectTrigger
                            className="w-32"
                            data-testid={`select-table-${col}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">— Skip —</SelectItem>
                            <SelectItem value="leads">leads</SelectItem>
                            <SelectItem value="clients">clients</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {d.targetTable === "__skip__" ? (
                          <span className="text-muted-foreground text-xs">
                            —
                          </span>
                        ) : (
                          <Select
                            disabled={isTerminal}
                            value={d.targetColumn}
                            onValueChange={(v) =>
                              update(col, { targetColumn: v })
                            }
                          >
                            <SelectTrigger
                              className="w-44"
                              data-testid={`select-column-${col}`}
                            >
                              <SelectValue placeholder="(choose)" />
                            </SelectTrigger>
                            <SelectContent>
                              {TARGET_OPTIONS[
                                d.targetTable as "leads" | "clients"
                              ].map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          disabled={
                            isTerminal || d.targetTable === "__skip__"
                          }
                          value={d.transformKind}
                          onValueChange={(v) =>
                            update(col, { transformKind: v })
                          }
                        >
                          <SelectTrigger
                            className="w-44"
                            data-testid={`select-transform-${col}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSFORM_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs">
                        {d.confidence != null && (
                          <Badge
                            variant="outline"
                            className={
                              d.confidence >=
                              (suggestionsData?.thresholds?.autoApply ?? 0.95)
                                ? "border-emerald-500 text-emerald-700"
                                : d.confidence >=
                                  (suggestionsData?.thresholds?.batchApprove ??
                                    0.7)
                                ? "border-amber-500 text-amber-700"
                                : "border-rose-500 text-rose-700"
                            }
                          >
                            {Math.round(d.confidence * 100)}%{" "}
                            {d.reason === "memory"
                              ? "memory"
                              : d.reason ?? ""}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          disabled={
                            isTerminal ||
                            d.targetTable === "__skip__" ||
                            !d.targetColumn
                          }
                          checked={d.rememberNow}
                          onChange={(e) =>
                            update(col, { rememberNow: e.target.checked })
                          }
                          data-testid={`checkbox-remember-${col}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => saveMut.mutate()}
            disabled={isTerminal || saveMut.isPending}
            data-testid="button-save-mappings"
          >
            {saveMut.isPending ? "Saving…" : "Save mappings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Audit timeline ───────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  action: string;
  actorUserId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

function AuditTimeline({ jobId }: { jobId: string }) {
  const { data, isLoading } = useQuery<AuditEntry[]>({
    queryKey: ["/api/platform/onboarding/jobs", jobId, "audit"],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/platform/onboarding/jobs/${jobId}/audit`
      );
      return res.json();
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit timeline</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {(data ?? []).map((row) => (
              <li
                key={row.id}
                className="border-l-2 border-muted pl-3"
                data-testid={`audit-${row.id}`}
              >
                <div className="font-mono text-xs text-muted-foreground">
                  {new Date(row.createdAt).toLocaleString()}
                </div>
                <div>
                  <strong>{row.action}</strong> —{" "}
                  <code className="text-xs">
                    {JSON.stringify(row.details)}
                  </code>
                </div>
              </li>
            ))}
            {(!data || data.length === 0) && (
              <li className="text-muted-foreground">
                No audit entries yet.
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
